import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { menuKeyForPath } from '../../utils/permissions';
import { useData } from '../../context/DataContext';
import { translateLocation } from '../../utils/translationUtils';
import { formatUserDisplayName } from '../../utils/userDisplayName';
import './Dashboard.css';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, can } = useAuth();
  const { data } = useData();
  const navigate = useNavigate();
  const { selectedLocation } = useOutletContext() || {};
  const [trendMonths, setTrendMonths] = useState(6);
  const [quickActionSearch, setQuickActionSearch] = useState('');

  const locationFilter = useMemo(() => {
    if (user?.role === 'manager') return (user.location || '').toLowerCase();
    if (selectedLocation && selectedLocation !== 'all') return selectedLocation.toLowerCase();
    return '';
  }, [selectedLocation, user?.location, user?.role]);

  const visibleUsers = useMemo(() => {
    const list = data.users || [];
    if (!locationFilter) return list;
    return list.filter((u) => (u.location || '').toLowerCase() === locationFilter);
  }, [data.users, locationFilter]);

  const visibleCourses = useMemo(() => {
    const list = data.courses || [];
    if (!locationFilter) return list;
    return list.filter((c) => (c.location || '').toLowerCase() === locationFilter);
  }, [data.courses, locationFilter]);

  const visibleEnrollments = useMemo(() => {
    const list = data.enrollments || [];
    if (!locationFilter) return list;
    return list.filter((e) => (e.course?.location || '').toLowerCase() === locationFilter);
  }, [data.enrollments, locationFilter]);

  const stats = {
    users: visibleUsers.length,
    courses: visibleCourses.length,
    enrollments: visibleEnrollments.length,
    studies: (data.studies || []).length
  };

  const userRoleStats = useMemo(() => {
    const initial = { admin: 0, manager: 0, instructor: 0, student: 0 };
    return visibleUsers.reduce((acc, item) => {
      const role = String(item.role || '').toLowerCase();
      if (Object.prototype.hasOwnProperty.call(acc, role)) {
        acc[role] += 1;
      }
      return acc;
    }, initial);
  }, [visibleUsers]);

  const enrollmentStatusStats = useMemo(() => {
    const initial = { active: 0, completed: 0, cancelled: 0 };
    return visibleEnrollments.reduce((acc, item) => {
      const status = String(item.status || '').toLowerCase();
      if (Object.prototype.hasOwnProperty.call(acc, status)) {
        acc[status] += 1;
      }
      return acc;
    }, initial);
  }, [visibleEnrollments]);

  const enrollmentTrend = useMemo(() => {
    const now = new Date();
    const monthKeys = [];
    for (let i = trendMonths - 1; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      monthKeys.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString('nl-NL', { month: 'short' })
      });
    }

    const counts = new Map(monthKeys.map((m) => [m.key, 0]));
    visibleEnrollments.forEach((row) => {
      const date = new Date(row.enrollmentDate || row.createdAt || Date.now());
      if (Number.isNaN(date.getTime())) return;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (counts.has(key)) counts.set(key, (counts.get(key) || 0) + 1);
    });

    const result = monthKeys.map((m) => ({ key: m.key, label: m.label, value: counts.get(m.key) || 0 }));
    const max = Math.max(1, ...result.map((r) => r.value));
    return { points: result, max };
  }, [trendMonths, visibleEnrollments]);

  const enrollmentStatusTotal = enrollmentStatusStats.active + enrollmentStatusStats.completed + enrollmentStatusStats.cancelled;
  const activePct = enrollmentStatusTotal ? Math.round((enrollmentStatusStats.active / enrollmentStatusTotal) * 100) : 0;
  const completedPct = enrollmentStatusTotal ? Math.round((enrollmentStatusStats.completed / enrollmentStatusTotal) * 100) : 0;

  const roleLabel = t(`roles.${user?.role || 'student'}`);
  const locationLabel = user?.location
    ? translateLocation(t, String(user.location).toLowerCase())
    : t('admin.allLocations');

  const roleMatrix = useMemo(() => {
    if (user?.role === 'admin') {
      return {
        allowed: [
          'Gebruikers en rollen beheren (inclusief activeren/deactiveren).',
          'Studies, cohorten, betalingen en website-content beheren.',
          'Documenten uploaden via Modulematerialen, Blog en Brochures.'
        ],
        blocked: []
      };
    }

    if (user?.role === 'manager') {
      return {
        allowed: [
          'Gebruikers beheren binnen eigen locatie (student/docent).',
          'Studies, inschrijvingen, betalingen en rapporten voor eigen locatie.',
          'Documenten en lesmateriaal beheren binnen locatiegebonden vakken.'
        ],
        blocked: [
          'Geen toegang tot admin-only acties zoals systeemrollen wijzigen.',
          'Geen toegang tot data buiten eigen locatie.'
        ]
      };
    }

    return {
      allowed: [
        'Huiswerk en cijfers beheren voor eigen toegewezen vakken.',
        'Eigen vakken en planning raadplegen.',
        'Lesmateriaal uploaden voor eigen vakken.'
      ],
      blocked: [
        'Geen toegang tot gebruikersbeheer, betalingen en website CMS.',
        'Geen toegang tot vakken die niet aan de docent gekoppeld zijn.'
      ]
    };
  }, [user?.role]);

  const menuItems = useMemo(() => {
    const declarationsAdmin = { key: 'admin-declarations', title: 'Verklaringen administratie', path: '/admin/administrative-declarations' };
    const annualGrades = { key: 'annual-grades', title: 'Jaarcijfers', path: '/admin/annual-grade-list' };
    const paymentReports = { key: 'payment-reports', title: 'Betaalrapporten', path: '/admin/report-payments' };

    if (user?.role === 'admin') {
      return [
        { key: 'users', title: t('admin.manageUsers'), path: '/admin/users' },
        { key: 'studies', title: t('admin.manageStudies'), path: '/admin/studies' },
        { key: 'assignments', title: t('admin.courseAssignments'), path: '/admin/course-assignments' },
        { key: 'payments', title: t('admin.payments'), path: '/admin/payments' },
        { key: 'materials', title: t('admin.moduleMaterials'), path: '/admin/module-materials' },
        { key: 'content', title: t('admin.websiteContent'), path: '/admin/content' },
        declarationsAdmin,
        annualGrades,
        paymentReports
      ];
    }

    const items = user?.role === 'manager'
      ? [
        { key: 'users', title: t('admin.manageUsers'), path: '/admin/users' },
        { key: 'registrations', title: t('admin.studyRegistrations'), path: '/admin/study-registrations' },
        { key: 'assignments', title: t('admin.courseAssignments'), path: '/admin/course-assignments' },
        { key: 'payments', title: t('admin.payments'), path: '/admin/payments' },
        { key: 'materials', title: t('admin.moduleMaterials'), path: '/admin/module-materials' },
        { key: 'grades', title: t('report.gradesReport', 'Cijfers Rapport'), path: '/admin/grades-entry' },
        declarationsAdmin,
        annualGrades,
        paymentReports
      ]
      : [
        { key: 'homework', title: t('nav.homework'), path: '/admin/homework' },
        { key: 'materials', title: t('admin.moduleMaterials'), path: '/admin/module-materials' },
        { key: 'calendar', title: t('admin.calendar'), path: '/admin/calendar' },
        { key: 'grades', title: t('report.gradesReport', 'Cijfers Rapport'), path: '/admin/grades-entry' },
        annualGrades
      ];

    // Alleen snelacties tonen waarvoor de (toegangs)rol leesrechten heeft.
    return items.filter((item) => can(menuKeyForPath(item.path), 'read'));
  }, [t, user?.role, can]);

  const uploadChecks = useMemo(() => ([
    {
      label: 'Admin modulematerialen',
      route: '/api/module-materials',
      storage: 'Backendsrv/uploads/module-materials/<courseId>/'
    },
    {
      label: 'Admin blog afbeeldingen',
      route: '/api/blogs',
      storage: 'Backendsrv/uploads/blogs/'
    },
    {
      label: 'Admin brochures/flyers',
      route: '/api/brochures en /api/content/flyers',
      storage: 'Backendsrv/uploads/brochures/ en Backendsrv/uploads/flyers/'
    },
    {
      label: 'Publieke inschrijvingsbijlagen',
      route: '/api/public/registration',
      storage: 'Backendsrv/uploads/registrations/'
    }
  ]), []);

  const filteredMenuItems = useMemo(() => {
    const q = quickActionSearch.trim().toLowerCase();
    if (!q) return menuItems;
    return menuItems.filter((item) => String(item.title || '').toLowerCase().includes(q));
  }, [menuItems, quickActionSearch]);

  const statCards = [
    { key: 'users', icon: 'U', value: stats.users, label: t('admin.users'), path: '/admin/users' },
    { key: 'courses', icon: 'V', value: stats.courses, label: t('admin.courses'), path: '/admin/studies' },
    { key: 'enrollments', icon: 'I', value: stats.enrollments, label: t('admin.enrollments'), path: '/admin/course-assignments' },
    { key: 'studies', icon: 'S', value: stats.studies, label: t('admin.studies'), path: '/admin/studies' }
  ];

  const openUsersByRole = (role) => {
    const params = new URLSearchParams();
    if (role) params.set('role', role);
    navigate(`/admin/users${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const openEnrollmentsByStatus = (status) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    navigate(`/admin/course-assignments${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const openEnrollmentsByMonth = (monthKey) => {
    const params = new URLSearchParams();
    params.set('month', monthKey);
    navigate(`/admin/course-assignments?${params.toString()}`);
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>{t('admin.dashboard')}</h1>
        <p className="welcome-text">{t('admin.welcome', { name: formatUserDisplayName(user) })}</p>
        <div className="dashboard-meta-row">
          <span className="dashboard-meta-pill">Rol: {roleLabel}</span>
          <span className="dashboard-meta-pill">Locatie: {locationLabel}</span>
        </div>
        <div className="dashboard-nav-pills">
          <button type="button" className="dashboard-nav-pill" onClick={() => document.getElementById('dash-charts')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Grafieken</button>
          <button type="button" className="dashboard-nav-pill" onClick={() => document.getElementById('dash-actions')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Snelle acties</button>
          <button type="button" className="dashboard-nav-pill" onClick={() => document.getElementById('dash-rights')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Rechten</button>
          <button type="button" className="dashboard-nav-pill" onClick={() => document.getElementById('dash-uploads')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Uploads</button>
        </div>
      </div>

      <div className="stats-grid">
        {statCards.map((card) => (
          <button
            key={card.key}
            type="button"
            className="stat-card stat-card-clickable"
            onClick={() => navigate(card.path)}
            aria-label={`${card.label}: ${card.value}`}
          >
            <span className="stat-icon">{card.icon}</span>
            <div className="stat-info">
              <h3>{card.value}</h3>
              <p>{card.label}</p>
            </div>
          </button>
        ))}
      </div>

      <div className="dashboard-content">
        <section id="dash-charts" className="recent-section charts-section">
          <h2>Dashboard Overzicht</h2>
          <div className="chart-range-buttons" role="group" aria-label="Kies trend periode">
            {[3, 6, 12].map((months) => (
              <button
                key={months}
                type="button"
                className={`chart-range-btn ${trendMonths === months ? 'active' : ''}`}
                onClick={() => setTrendMonths(months)}
              >
                {months} maanden
              </button>
            ))}
          </div>
          <div className="charts-grid">
            <article className="chart-card">
              <h3>Gebruikers per rol</h3>
              <div className="role-bars">
                {[
                  ['Studenten', userRoleStats.student, 'var(--bar-student)', 'student'],
                  ['Docenten', userRoleStats.instructor, 'var(--bar-instructor)', 'instructor'],
                  ['Managers', userRoleStats.manager, 'var(--bar-manager)', 'manager'],
                  ['Admins', userRoleStats.admin, 'var(--bar-admin)', 'admin']
                ].map(([label, value, color, role]) => {
                  const maxValue = Math.max(1, userRoleStats.student, userRoleStats.instructor, userRoleStats.manager, userRoleStats.admin);
                  const pct = Math.round((Number(value) / maxValue) * 100);
                  return (
                    <button type="button" key={label} className="role-bar-row role-bar-row-clickable" onClick={() => openUsersByRole(role)}>
                      <span>{label}</span>
                      <div className="role-bar-track"><div className="role-bar-fill" style={{ width: `${pct}%`, background: color }} /></div>
                      <strong>{value}</strong>
                    </button>
                  );
                })}
              </div>
            </article>

            <article className="chart-card">
              <h3>Inschrijvingsstatus</h3>
              <div className="status-donut-wrap">
                <div
                  className="status-donut"
                  role="button"
                  tabIndex={0}
                  onClick={() => openEnrollmentsByStatus('')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openEnrollmentsByStatus('');
                    }
                  }}
                  style={{
                    background: `conic-gradient(var(--status-active) 0 ${activePct}%, var(--status-completed) ${activePct}% ${activePct + completedPct}%, var(--status-cancelled) ${activePct + completedPct}% 100%)`
                  }}
                >
                  <div className="status-donut-center">{enrollmentStatusTotal}</div>
                </div>
                <div className="status-legend">
                  <button type="button" className="status-legend-btn" onClick={() => openEnrollmentsByStatus('active')}><i style={{ background: 'var(--status-active)' }} /> Actief: {enrollmentStatusStats.active}</button>
                  <button type="button" className="status-legend-btn" onClick={() => openEnrollmentsByStatus('completed')}><i style={{ background: 'var(--status-completed)' }} /> Afgerond: {enrollmentStatusStats.completed}</button>
                  <button type="button" className="status-legend-btn" onClick={() => openEnrollmentsByStatus('cancelled')}><i style={{ background: 'var(--status-cancelled)' }} /> Geannuleerd: {enrollmentStatusStats.cancelled}</button>
                </div>
              </div>
            </article>

            <article className="chart-card chart-card-wide">
              <h3>Inschrijvingen laatste {trendMonths} maanden</h3>
              <div className="trend-chart">
                {enrollmentTrend.points.map((point) => (
                  <button type="button" key={point.key} className="trend-col trend-col-clickable" onClick={() => openEnrollmentsByMonth(point.key)}>
                    <div className="trend-value">{point.value}</div>
                    <div className="trend-bar-track">
                      <div className="trend-bar-fill" style={{ height: `${Math.max(8, Math.round((point.value / enrollmentTrend.max) * 100))}%` }} />
                    </div>
                    <div className="trend-label">{point.label}</div>
                  </button>
                ))}
              </div>
            </article>
          </div>
        </section>

        <section id="dash-actions" className="quick-actions">
          <h2>{t('admin.quickActions')}</h2>
          <input
            className="quick-actions-search"
            type="search"
            value={quickActionSearch}
            onChange={(e) => setQuickActionSearch(e.target.value)}
            placeholder="Zoek actie..."
            aria-label="Zoek in snelle acties"
          />
          <div className="courses-list">
            {filteredMenuItems.map((item) => (
              <Link key={item.key} to={item.path} className="course-card">
                <div className="course-header">
                  <span className="course-name">{item.title}</span>
                </div>
              </Link>
            ))}
            {filteredMenuItems.length === 0 && (
              <p className="no-data">Geen acties gevonden voor deze zoekterm.</p>
            )}
          </div>
        </section>

        <section id="dash-rights" className="recent-section">
          <h2>Rechten Check ({roleLabel})</h2>
          <div className="rights-grid">
            <article className="rights-card rights-allowed">
              <h3>Toegestaan</h3>
              <ul>
                {roleMatrix.allowed.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article className="rights-card rights-blocked">
              <h3>Niet toegestaan</h3>
              {roleMatrix.blocked.length ? (
                <ul>
                  {roleMatrix.blocked.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p>Geen functionele blokkades binnen admin-rolniveau.</p>
              )}
            </article>
          </div>
        </section>

        <section id="dash-uploads" className="recent-section upload-check-section">
          <h2>Document Upload Check</h2>
          <p className="upload-check-note">
            Uploads worden via backend opgeslagen onder de `Backendsrv/uploads/` structuur en via `/api/uploads/...` ontsloten.
          </p>
          <div className="upload-check-list">
            {uploadChecks.map((item) => (
              <article key={item.label} className="upload-check-item">
                <h3>{item.label}</h3>
                <p><strong>Route:</strong> {item.route}</p>
                <p><strong>Opslag:</strong> {item.storage}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
